/**
 * Nosto Category Merchandising
 *
 * Calls Nosto's Search API on collection pages to apply category merchandising
 * rules and reorder the product grid according to Nosto's ranked results.
 *
 * Hooks into the existing facets.js AJAX filter flow via MutationObserver so
 * merchandised ordering is re-applied after every filter/sort update.
 *
 * Skips ordering when:
 *  - The user has explicitly changed the sort order away from the default.
 *  - The page number is greater than 1.
 *  - nostojs does not become available within NOSTO_TIMEOUT_MS.
 *  - api.search is not available (Search module not enabled on account).
 *  - Nosto's results cover fewer than 50% of visible DOM products (collection
 *    is likely excluded from merchandising rules).
 */

(function () {
  'use strict';

  var NOSTO_TIMEOUT_MS = 3000;
  var nostoCheckIntervalMs = 100;

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    var metaEl = document.getElementById('nosto-category-meta');
    if (!metaEl) return;

    var categoryId   = metaEl.getAttribute('data-category-id');
    var categoryPath = metaEl.getAttribute('data-category-title') || metaEl.getAttribute('data-category-path');
    var defaultSort  = metaEl.getAttribute('data-default-sort');

    if (!categoryId || !categoryPath) return;

    var urlParams    = new URLSearchParams(window.location.search);
    var activeSortBy = urlParams.get('sort_by');

    // Skip when the user has explicitly chosen a non-default sort order.
    if (activeSortBy && activeSortBy !== defaultSort) return;

    // Skip on page 2+. DOM-reorder only makes sense when Nosto and Shopify
    // are operating on the same set of products (page 1).
    var currentPage = parseInt(urlParams.get('page') || '1', 10);
    if (currentPage > 1) return;

    applyNostoOrdering(categoryId, categoryPath);
    observeProductGridChanges(categoryId, categoryPath, defaultSort);
  }

  // ─── Core ordering logic ──────────────────────────────────────────────────

  function applyNostoOrdering(categoryId, categoryPath) {
    var productList = document.getElementById('product-grid');
    if (!productList) return;

    var productCards = productList.querySelectorAll('li[data-product-id]');
    if (!productCards.length) return;

    // Show Shopify's built-in loading overlay while waiting for Nosto.
    var collectionEl = productList.closest('.collection');
    if (collectionEl) collectionEl.classList.add('loading');

    var resolved = false;

    // Fallback: remove loading state if Nosto doesn't respond in time.
    var fallbackTimeout = setTimeout(function () {
      if (!resolved) {
        resolved = true;
        if (collectionEl) collectionEl.classList.remove('loading');
      }
    }, NOSTO_TIMEOUT_MS);

    waitForNostoJs(function () {
      window.nostojs(function (api) {
        // Guard: Search module may not be enabled on this Nosto account.
        if (typeof api.search !== 'function') {
          if (!resolved) {
            resolved = true;
            clearTimeout(fallbackTimeout);
            if (collectionEl) collectionEl.classList.remove('loading');
          }
          return;
        }

        var requestSize = parseInt(
          document.getElementById('nosto-category-meta').getAttribute('data-total-products') || '200',
          10
        );

        api
          .search(
            {
              products: {
                categoryId:   categoryId,
                categoryPath: categoryPath,
                fields: ['productId'],
                size: requestSize,
                from: 0,
              },
            },
            {
              track:    'category',
              redirect: false,
            }
          )
          .then(function (result) {
            if (resolved) return;
            resolved = true;
            clearTimeout(fallbackTimeout);

            var hits = result && result.products && result.products.hits;

            if (!hits || !hits.length) {
              if (collectionEl) collectionEl.classList.remove('loading');
              return;
            }

            var orderedIds = hits.map(function (hit) {
              // Normalise: strip any "shopify-" prefix Nosto may add.
              return String(hit.productId).replace(/^shopify-/, '');
            });

            // Sanity-check: if Nosto's results cover fewer than 50% of the
            // visible DOM products the collection is likely excluded from
            // Nosto's merchandising rules (or has no rule configured).
            // Applying a low-coverage reorder makes the order worse, not
            // better — so bail out and leave Shopify's default order intact.
            var nostoIdSet = {};
            orderedIds.forEach(function (id) { nostoIdSet[id] = true; });

            var domProductIds = Array.prototype.map.call(
              productList.querySelectorAll('li[data-product-id]'),
              function (li) { return String(li.getAttribute('data-product-id')).replace(/^shopify-/, ''); }
            );
            var domMatchCount = domProductIds.filter(function (id) { return nostoIdSet[id]; }).length;
            var matchRatio    = domProductIds.length ? domMatchCount / domProductIds.length : 0;

            if (matchRatio < 0.5) {
              if (collectionEl) collectionEl.classList.remove('loading');
              return;
            }

            reorderProductCards(productList, orderedIds, nostoIdSet);
            if (collectionEl) collectionEl.classList.remove('loading');
          })
          .catch(function () {
            if (resolved) return;
            resolved = true;
            clearTimeout(fallbackTimeout);
            if (collectionEl) collectionEl.classList.remove('loading');
          });
      });
    }, NOSTO_TIMEOUT_MS);
  }

  /**
   * Reorders <li data-product-id> elements inside productList to match
   * the order of IDs returned by Nosto. DOM products outside Nosto's result
   * set (e.g. beyond the 250-product cap) are appended after ranked products.
   */
  function reorderProductCards(productList, orderedIds, nostoIdSet) {
    var cardMap = {};
    productList.querySelectorAll('li[data-product-id]').forEach(function (li) {
      var rawId = li.getAttribute('data-product-id');
      cardMap[String(rawId).replace(/^shopify-/, '')] = li;
    });

    // First pass: append products in Nosto's ranked order.
    orderedIds.forEach(function (id) {
      if (cardMap[id]) productList.appendChild(cardMap[id]);
    });

    // Second pass: append any DOM products Nosto didn't return (beyond its
    // cap) so they appear after ranked products rather than floating at the
    // top in Shopify's default order.
    Object.keys(cardMap).forEach(function (id) {
      if (!nostoIdSet[id]) productList.appendChild(cardMap[id]);
    });
  }

  // ─── MutationObserver for AJAX filter updates ─────────────────────────────

  /**
   * Watches #ProductGridContainer for DOM replacements triggered by
   * facets.js (AJAX filtering / sorting). Re-applies Nosto ordering after
   * each replacement, unless the user has changed the sort order.
   */
  function observeProductGridChanges(categoryId, categoryPath, defaultSort) {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length) {
          var urlParams    = new URLSearchParams(window.location.search);
          var activeSortBy = urlParams.get('sort_by');
          if (activeSortBy && activeSortBy !== defaultSort) break;
          applyNostoOrdering(categoryId, categoryPath);
          break;
        }
      }
    });

    observer.observe(container, { childList: true, subtree: false });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Polls until window.nostojs is defined, then calls callback.
   * Gives up after maxWaitMs milliseconds.
   */
  function waitForNostoJs(callback, maxWaitMs) {
    if (window.nostojs) {
      callback();
      return;
    }
    var startTime = Date.now();
    var interval  = setInterval(function () {
      if (window.nostojs) {
        clearInterval(interval);
        callback();
      } else if (Date.now() - startTime >= maxWaitMs) {
        clearInterval(interval);
      }
    }, nostoCheckIntervalMs);
  }
})();
